"use client";

import { Monitor, Moon, Sun } from "lucide-react";
import * as React from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";

const themes = [
  { value: "dark", icon: Moon, label: "Dark" },
  { value: "light", icon: Sun, label: "Light" },
  { value: "system", icon: Monitor, label: "System" },
] as const;

export function ThemeSwitcher({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => setMounted(true), []);

  const cycleTheme = () => {
    const currentIndex = themes.findIndex((t) => t.value === theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex].value);
  };

  if (!mounted) {
    return (
      <button
        type="button"
        className={cn(
          "glass text-muted-foreground hover:text-foreground inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
          className,
        )}
        aria-label="Toggle theme"
      >
        <Moon className="h-4 w-4" />
      </button>
    );
  }

  const current = themes.find((t) => t.value === theme) ?? themes[0];
  const Icon = current.icon;

  return (
    <button
      type="button"
      onClick={cycleTheme}
      className={cn(
        "glass glass-hover text-muted-foreground hover:text-foreground inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
        className,
      )}
      aria-label={`Theme: ${current.label}. Click to change.`}
      title={current.label}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
